import { AppDispatch } from "../../store/store";
import axiosInstance from "../../utility/axios";
import { getErrorMessage } from "../apiError";

import {
  ILawyer,
  ILawyerCreate,
  ILawyerCreateResponse,
  ILawyerResponse,
  ILawyersResponse,
  IToggleLawyerStatusResponse,
} from "../../store/types/lawyers";
import {
  disableLawyerLoading,
  createLawyer,
  deleteLawyer,
  editLawyer,
  setLawyer,
  setLawyerLoading,
  setLawyers,
  setLoadingLawyerId,
  updateLawyerStatus,
} from "../../store/slices/lawyers";
import { toast } from "sonner";
import { clearLawyer } from "../../store/slices/lawyers";

const lawyerUrl = "/api/v1/admin";

export const get_lawyers = async (
  dispatch: AppDispatch,
  offset: number = 0,
  limit: number = 20,
) => {
  dispatch(setLawyerLoading());

  try {
    const response = await axiosInstance.get<ILawyersResponse>(
      `${lawyerUrl}/lawyers`,
      { params: { offset, limit } },
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load lawyers");
    }
    dispatch(setLawyers(response.data));
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to load lawyers"));
  } finally {
    dispatch(disableLawyerLoading());
  }
};

export const get_lawyer_options = async () => {
  const limit = 100;
  let offset = 0;
  const lawyers: ILawyer[] = [];
  let total = 0;

  try {
    do {
      const response = await axiosInstance.get<ILawyersResponse>(
        `${lawyerUrl}/lawyers`,
        { params: { offset, limit } },
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to load lawyers");
      }

      const page = response.data.data.items;
      lawyers.push(...page);
      total = response.data.data.total;
      offset += page.length;

      if (page.length === 0 && offset < total) {
        throw new Error("Lawyer list pagination returned no results");
      }
    } while (offset < total);

    return lawyers;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to load lawyers"));
    return null;
  }
};

export const get_lawyer = async (
  dispatch: AppDispatch,
  id: string,
): Promise<boolean> => {
  dispatch(setLawyerLoading());

  try {
    const response = await axiosInstance.get<ILawyerResponse>(
      `${lawyerUrl}/lawyers/${id}`,
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load lawyer");
    }
    dispatch(setLawyer(response.data.data));
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to load lawyer details"));
    return false;
  } finally {
    dispatch(disableLawyerLoading());
  }
};

export const clear_lawyer = (dispatch: AppDispatch) => {
  dispatch(clearLawyer());
};

export const toggle_lawyer_status = async (
  dispatch: AppDispatch,
  id: number,
  isActive: boolean,
) => {
  dispatch(setLoadingLawyerId(id));

  try {
    const response = await axiosInstance.patch<IToggleLawyerStatusResponse>(
      `${lawyerUrl}/lawyers/${id}/status`,
      {
        is_active: isActive,
      },
    );

    if (!response.data.success) {
      throw new Error(
        response.data.message || "Failed to update lawyer status",
      );
    }
    dispatch(
      updateLawyerStatus({
        id: response.data.data.id,
        is_active: response.data.data.is_active,
      }),
    );
    toast.success(response.data.message || "Lawyer status updated");
    return response.data;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to update lawyer status"));
  } finally {
    dispatch(setLoadingLawyerId(null));
  }
};

// create lawyer

export const create_lawyer = async (
  dispatch: AppDispatch,
  lawyerData: ILawyerCreate,
): Promise<boolean> => {
  dispatch(setLawyerLoading());
  try {
    const response = await axiosInstance.post<ILawyerCreateResponse>(
      `${lawyerUrl}/lawyers`,
      lawyerData,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to create lawyer");
    }
    dispatch(createLawyer(response.data.data));
    toast.success(response.data.message || "Lawyer created successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to create lawyer"));
    return false;
  } finally {
    dispatch(disableLawyerLoading());
  }
};

export const edit_lawyer = async (
  dispatch: AppDispatch,
  lawyer_id: number,
  lawyerData: Partial<ILawyerCreate>,
): Promise<boolean> => {
  dispatch(setLawyerLoading());
  try {
    const response = await axiosInstance.put<ILawyerCreateResponse>(
      `${lawyerUrl}/lawyers/${lawyer_id}`,
      lawyerData,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to update lawyer");
    }
    dispatch(editLawyer(response.data.data));
    toast.success(response.data.message || "Lawyer updated successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to update lawyer"));
    return false;
  } finally {
    dispatch(disableLawyerLoading());
  }
};

export const delete_lawyer = async (dispatch: AppDispatch, id: number) => {
  dispatch(setLoadingLawyerId(id));
  try {
    const response = await axiosInstance.delete<ILawyerCreateResponse>(
      `${lawyerUrl}/lawyers/${id}`,
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delete lawyer");
    }
    dispatch(deleteLawyer(id));
    toast.success(response.data.message || "Lawyer deleted successfully");
    return true;
  } catch (err) {
    toast.error(getErrorMessage(err, "Failed to delete lawyer"));
    return false;
  } finally {
    dispatch(setLoadingLawyerId(null));
  }
};
